
'use client';

import { useState, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCategory } from '@/context/CategoryContext';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AddCategoryDialogProps {
  children: ReactNode;
}

export function AddCategoryDialog({ children }: AddCategoryDialogProps) {
  const { createCategory } = useCategory();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({ title: "Name required", description: "Please enter the category name.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      await createCategory({ name });
      setName('');
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to create category:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const onOpenChange = (open: boolean) => {
    if (!open) {
        setName('');
    }
    setIsOpen(open);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Category</DialogTitle>
          <DialogDescription>
            Enter the name for the new project category.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
            <div className="space-y-2">
                <Label htmlFor="category-name">Category Name</Label>
                <Input 
                  id="category-name" 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Concept Art"
                />
            </div>
        </div>
        <DialogFooter>
            <DialogClose asChild><Button type="button" variant="secondary">Cancel</Button></DialogClose>
            <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add
            </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
